import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  Label,
  SelectV2,
  Button,
  Checkbox,
  toast,
  Spinner,
} from '@lionstep/ui';

// Helper
import { collaboratorsOptionMapper } from '../../../../../dashboard.helper';

// Queries
import {
  useAddUserToJobMutation,
  useAddJobMailingPermissionsMutation,
  useSendUserNewJobEmailMutation,
} from '../../../../../../../queries/jobQueries';

// Styles
import styles from './inviteExistingCollaborator.module.scss';

const InviteExistingCollaborator = ({
  job,
  collaborators,
  collaboratorsNotOnJob,
}) => {
  const { t } = useTranslation();

  const [shouldAddMailingPermission, setShouldAddMailingPermission] =
    useState(true);
  const [selectedUserOption, setSelectedUserOption] = useState({
    label: '',
    value: null,
  });

  // Add User to Job
  const { mutateAsync: addUserToJob, isLoading: isLoadingAddUserToJob } =
    useAddUserToJobMutation({
      onError: (error) => {
        toast.error('There was an error while adding job user.', {
          id: 'POST_ADD_JOB_USER',
          statusCode: error?.response?.status,
        });
      },
    });

  // Add Job Mailing Permission
  const {
    mutateAsync: addJobMailingPermission,
    isLoading: isLoadingAddJobMailingPermission,
  } = useAddJobMailingPermissionsMutation({
    onError: (error) => {
      toast.error('There was an error while adding job mailing permission.', {
        id: 'POST_ADD_JOB_MAILING_PERMISSION',
        statusCode: error?.response?.status,
      });
    },
  });

  // Send User New Job Email
  const {
    mutateAsync: sendUserNewJobEmail,
    isLoading: isLoadingSendUserNewJobEmail,
  } = useSendUserNewJobEmailMutation({});

  const handleOnAddCollaborator = async () => {
    if (selectedUserOption.value) {
      const selectedUser = collaborators.find(
        (user) => user.local_id === selectedUserOption.value,
      );

      await addUserToJob({
        jobId: job.id,
        userId: selectedUser.local_id,
      });

      if (shouldAddMailingPermission) {
        await addJobMailingPermission({
          jobId: job.id,
          data: { user_id: selectedUser.local_id },
        });
      }

      await sendUserNewJobEmail({
        user: selectedUser,
        jobTitle: job.opening_title,
      });

      setSelectedUserOption({ label: '', value: null });
    }
  };

  const isLoading =
    isLoadingAddUserToJob ||
    isLoadingSendUserNewJobEmail ||
    isLoadingAddJobMailingPermission;

  return (
    <>
      <Grid.Row gutter={15} className={styles.inviteExistingForm}>
        {isLoading && <Spinner position={'absolute'} size={100} />}
        <Grid.Col xs={24} sm={16} lg={10} xl={8}>
          <Label htmlFor="inviteCollaborators">Select collaborator</Label>
          <SelectV2
            name="inviteCollaborators"
            placeholder={t('invite_collaborators')}
            options={collaboratorsOptionMapper(collaboratorsNotOnJob)}
            value={selectedUserOption}
            onChange={setSelectedUserOption}
          />
        </Grid.Col>

        <Grid.Col xs={24} sm={8} lg={6} xl={4}>
          <Button
            className={styles.submitButton}
            disabled={!selectedUserOption.value || isLoading}
            onClick={handleOnAddCollaborator}
          >
            {t('share_job')}
          </Button>
        </Grid.Col>
      </Grid.Row>

      <Grid.Row className={styles.sendCollaboratorStatus}>
        <Grid.Col xs={24}>
          <Checkbox
            inputAlign
            name="userJobNotifications"
            checked={shouldAddMailingPermission}
            label={t('send_email_updates')}
            onChange={(e) => setShouldAddMailingPermission(e.target.checked)}
          />
        </Grid.Col>
      </Grid.Row>
    </>
  );
};

InviteExistingCollaborator.defaultProps = {
  job: {},
};

InviteExistingCollaborator.propTypes = {
  collaborators: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  collaboratorsNotOnJob: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  job: PropTypes.shape(),
};

export default InviteExistingCollaborator;
